import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { DetailButton } from './detail'
import './detail/detail.css'
import './sessionAlerts.css'

interface SessionWarningAlertProps {
  open: boolean
  secondsRemaining: number
  onRenew: () => void
  onDismiss: () => void
}

export const SessionWarningAlert: React.FC<SessionWarningAlertProps> = ({
  open,
  secondsRemaining,
  onRenew,
  onDismiss,
}) => {
  const { t } = useTranslation()

  const minutes = Math.floor(secondsRemaining / 60)
  const seconds = secondsRemaining % 60
  const countdownLabel = `${minutes}:${String(seconds).padStart(2, '0')}`
  const progress = Math.max(0, Math.min(1, secondsRemaining / 300))

  if (!open) return null

  return (
    <div
      className="sa-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sw-title"
    >
      <div className="sa-card sa-card--enter">
        <div className="incyia-modal-head">
          <div className="incyia-modal-icon incyia-modal-icon--warn" aria-hidden>
            <TimerOutlinedIcon sx={{ fontSize: 22 }} />
          </div>
          <div>
            <p className="sa-eyebrow">
              {t('sessionAlerts.sessionWarning.badge', 'Security notice')}
            </p>
            <h3 id="sw-title">
              {t(
                'sessionAlerts.sessionWarning.title',
                'Session expiring soon'
              )}
            </h3>
            <p>
              {t(
                'sessionAlerts.sessionWarning.message',
                'Your session is about to expire. Renew now to continue working without interruption.'
              )}
            </p>
          </div>
          <button
            type="button"
            className="incyia-modal-x"
            onClick={onDismiss}
            aria-label={t('sessionAlerts.sessionWarning.dismiss', 'Dismiss')}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        <div className="incyia-modal-body">
          <div className="incyia-modal-countdown">
            <span className="incyia-modal-countdown-label">
              {t('sessionAlerts.sessionWarning.left', 'Time left')}
            </span>
            <span className="incyia-modal-countdown-value">{countdownLabel}</span>
            <div className="sa-meter" aria-hidden>
              <div
                className="sa-meter-fill"
                style={{ transform: `scaleX(${progress})` }}
              />
            </div>
          </div>
        </div>

        <div className="incyia-modal-foot">
          <DetailButton variant="ghost" onClick={onDismiss}>
            {t('sessionAlerts.sessionWarning.dismiss', 'Dismiss')}
          </DetailButton>
          <DetailButton onClick={onRenew}>
            {t('sessionAlerts.sessionWarning.renew', 'Renew session')}
          </DetailButton>
        </div>
      </div>
    </div>
  )
}

interface SessionExpiredAlertProps {
  open: boolean
  onLogin: () => void
}

export const SessionExpiredAlert: React.FC<SessionExpiredAlertProps> = ({
  open,
  onLogin,
}) => {
  const { t } = useTranslation()

  if (!open) return null

  return (
    <div
      className="sa-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="se-title"
    >
      <div className="sa-card sa-card--enter">
        <div className="incyia-modal-head">
          <div
            className="incyia-modal-icon incyia-modal-icon--danger"
            aria-hidden
          >
            <LockOutlinedIcon sx={{ fontSize: 22 }} />
          </div>
          <div>
            <p className="sa-eyebrow">
              {t(
                'sessionAlerts.sessionExpired.badge',
                'Authentication required'
              )}
            </p>
            <h3 id="se-title">
              {t('sessionAlerts.sessionExpired.title', 'Session expired')}
            </h3>
            <p>
              {t(
                'sessionAlerts.sessionExpired.message',
                'Your session has ended for security reasons. Please log in again to continue.'
              )}
            </p>
          </div>
        </div>

        <div className="incyia-modal-foot">
          <DetailButton onClick={onLogin} className="sa-full-btn">
            {t('sessionAlerts.sessionExpired.loginButton', 'Log in again')}
          </DetailButton>
        </div>
      </div>
    </div>
  )
}

interface SessionAlertManagerProps {
  onRenew: () => void
  onLogin: () => void
  sessionExpired?: boolean
  sessionExpiring?: boolean
  initialSeconds?: number
}

export const SessionAlertManager: React.FC<SessionAlertManagerProps> = ({
  onRenew,
  onLogin,
  sessionExpired = false,
  sessionExpiring = false,
  initialSeconds = 300,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds)
  const [dismissed, setDismissed] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (sessionExpiring && !dismissed) {
      setSecondsLeft(initialSeconds)
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0))
      }, 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [sessionExpiring, dismissed, initialSeconds])

  useEffect(() => {
    if (!sessionExpiring) setDismissed(false)
  }, [sessionExpiring])

  const handleRenew = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setDismissed(true)
    onRenew()
  }

  if (sessionExpired) {
    return <SessionExpiredAlert open onLogin={onLogin} />
  }

  if (sessionExpiring && !dismissed) {
    return (
      <SessionWarningAlert
        open
        secondsRemaining={secondsLeft}
        onRenew={handleRenew}
        onDismiss={() => setDismissed(true)}
      />
    )
  }

  return null
}
